<template>
  <!-- Entrega: solo tiene sentido cuando hay PRODUCTOS (un servicio no se envia). -->
  <div class="iq-card iq-card-sm" v-if="store.products.length">
    <div class="card-body">
      <h5 class="mb-3">{{ $t('pos.lbl_delivery') }}</h5>

      <div class="btn-group w-100 mb-2" role="group">
        <button
          type="button"
          class="btn pos-tap"
          :class="store.delivery_type === 'pickup' ? 'btn-primary' : 'btn-outline-secondary'"
          @click="setType('pickup')"
        >
          <i class="fa-solid fa-store me-1" aria-hidden="true"></i>{{ $t('pos.lbl_pickup') }}
        </button>
        <button
          type="button"
          class="btn pos-tap"
          :class="store.delivery_type === 'delivery' ? 'btn-primary' : 'btn-outline-secondary'"
          @click="setType('delivery')"
        >
          <i class="fa-solid fa-truck me-1" aria-hidden="true"></i>{{ $t('pos.lbl_delivery_home') }}
        </button>
      </div>

      <template v-if="store.delivery_type === 'delivery'">
        <!-- Sin cliente aun: la direccion sale del cliente. -->
        <p class="text-muted small mb-0" v-if="!store.customer">{{ $t('pos.lbl_delivery_customer_first') }}</p>

        <template v-else>
          <div v-if="loading" class="text-muted small">
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t('pos.lbl_loading') }}
          </div>

          <template v-else>
            <template v-if="addresses.length">
              <label class="form-label small mb-1" for="pos-address">{{ $t('pos.lbl_shipping_address') }}</label>
              <select id="pos-address" class="form-select form-select-sm" v-model="store.shipping_address_id">
                <option :value="null">{{ $t('pos.lbl_select_address') }}</option>
                <option v-for="address in addresses" :key="`addr-${address.id}`" :value="address.id">
                  {{ address.label }}{{ address.is_primary ? ` · ${$t('pos.lbl_primary_address')}` : '' }}
                </option>
              </select>
            </template>

            <AlertMessage class="mb-0" variant="warning" v-else-if="!creating" :message="$t('pos.lbl_no_addresses')" />

            <!-- Alta de direccion al vuelo -->
            <button type="button" class="btn btn-sm btn-outline-primary w-100 mt-2 pos-tap" v-if="!creating" @click="creating = true">
              <i class="fa-solid fa-plus me-1" aria-hidden="true"></i>{{ $t('pos.lbl_add_address') }}
            </button>

            <div class="row g-2 mt-1" v-else>
              <div class="col-12">
                <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_address_line_1')" v-model="form.address_line_1" />
              </div>
              <div class="col-md-6">
                <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_city')" v-model="form.city" />
              </div>
              <div class="col-md-6">
                <input type="text" class="form-control form-control-sm" :placeholder="$t('pos.lbl_state')" v-model="form.state" />
              </div>
              <div class="col-12" v-if="addressError">
                <AlertMessage class="mb-0" :message="addressError" />
              </div>
              <div class="col-12 d-flex gap-2">
                <button type="button" class="btn btn-sm btn-secondary pos-tap" @click="cancelCreate">{{ $t('pos.lbl_cancel') }}</button>
                <button type="button" class="btn btn-sm btn-primary pos-tap" :disabled="saving || !form.address_line_1.trim()" @click="saveAddress">
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t('pos.lbl_save_address') }}
                </button>
              </div>
            </div>

            <p class="text-muted small mt-2 mb-0" v-if="!creating">{{ $t('pos.lbl_delivery_note') }}</p>
          </template>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import { usePos } from '../../store/pos'
import { posRequest } from '../usePosRequest'
import { POS_CUSTOMER_ADDRESSES, POS_CUSTOMER_ADDRESS_STORE } from '../constants/pos'

const store = usePos()
const { t } = useI18n()

const addresses = ref([])
const loading = ref(false)

// Alta de direccion al vuelo.
const creating = ref(false)
const saving = ref(false)
const addressError = ref('')
const form = reactive({ address_line_1: '', city: '', state: '' })

const setType = (type) => {
  store.delivery_type = type
  if (type !== 'delivery') store.shipping_address_id = null
  if (type === 'delivery') loadAddresses()
}

const loadAddresses = async () => {
  if (!store.customer) return

  loading.value = true
  const res = await posRequest(POS_CUSTOMER_ADDRESSES({ user_id: store.customer.id }))
  loading.value = false

  addresses.value = res.ok && Array.isArray(res.data) ? res.data : []

  // Preselecciona la principal (o la unica) para ahorrar un tap en mostrador.
  if (!store.shipping_address_id && addresses.value.length) {
    const primary = addresses.value.find((address) => address.is_primary)
    store.shipping_address_id = primary ? primary.id : addresses.value[0].id
  }
}

const cancelCreate = () => {
  creating.value = false
  addressError.value = ''
  Object.assign(form, { address_line_1: '', city: '', state: '' })
}

const saveAddress = async () => {
  if (!store.customer || !form.address_line_1.trim()) return

  saving.value = true
  addressError.value = ''

  const res = await posRequest(POS_CUSTOMER_ADDRESS_STORE(), {
    user_id: store.customer.id,
    address_line_1: form.address_line_1.trim(),
    city: form.city.trim() || null,
    state: form.state.trim() || null
  })

  saving.value = false

  if (!res.ok) {
    addressError.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  // Se anade a la lista y se preselecciona la nueva direccion.
  if (res.data && res.data.id) {
    addresses.value = [res.data, ...addresses.value]
    store.shipping_address_id = res.data.id
  }
  cancelCreate()
}

// Si cambia el cliente con "envio" activo, se recargan sus direcciones.
watch(
  () => store.customer?.id,
  () => {
    store.shipping_address_id = null
    addresses.value = []
    cancelCreate()
    if (store.delivery_type === 'delivery' && store.customer) loadAddresses()
  }
)
</script>
